import { Controller } from "@hotwired/stimulus"

// Parcours du diagnostic de maturité. Lit les questions + métadonnées (axes,
// libellés de niveaux) embarquées en JSON, gère la progression SANS révélation
// (pas de bonne/mauvaise réponse), calcule le profil par axe côté client pour le
// teaser (niveau global + radar SVG). Le serveur recalcule les niveaux et rend le
// détail + les recommandations (verrouillés derrière l'email).
export default class extends Controller {
  static targets = [
    "data", "meta", "quiz", "progressAxis", "progressStep", "prompt",
    "choices", "result", "overall", "radar", "answersField"
  ]

  connect() {
    this.questions = JSON.parse(this.dataTarget.textContent)
    const meta = JSON.parse(this.metaTarget.textContent)
    this.axes = meta.axes
    this.levelLabels = meta.levels
    this.index = 0
    this.answers = []
    this.renderQuestion()
  }

  renderQuestion() {
    const q = this.questions[this.index]
    const axis = this.axes.find((a) => a.key === q.axis)
    this.progressAxisTarget.textContent = axis ? axis.label : ""
    this.progressStepTarget.textContent = `${this.index + 1} / ${this.questions.length}`
    this.promptTarget.textContent = q.prompt
    this.choicesTarget.innerHTML = ""

    q.choices.forEach((choice, level) => {
      const button = document.createElement("button")
      button.type = "button"
      button.textContent = choice
      button.className =
        "block w-full rounded-lg border border-gray-300 px-4 py-3 text-left hover:border-primary-400 dark:border-gray-700"
      button.addEventListener("click", () => this.answer(level))
      this.choicesTarget.appendChild(button)
    })
  }

  answer(chosen) {
    this.answers.push({ id: this.questions[this.index].id, chosen: chosen })
    this.index++
    if (this.index < this.questions.length) {
      this.renderQuestion()
    } else {
      this.showResult()
    }
  }

  showResult() {
    this.quizTarget.classList.add("hidden")
    const levels = this.axisLevels()
    this.overallTarget.textContent = this.overallLabel(levels)
    this.radarTarget.innerHTML = this.radarSvg(levels)
    this.resultTarget.classList.remove("hidden")
  }

  prepareSubmit() {
    this.answersFieldTarget.value = JSON.stringify(this.answers)
  }

  // { key: niveau(1..4) } : moyenne arrondie des niveaux choisis par axe.
  axisLevels() {
    const byAxis = {}
    this.answers.forEach((a) => {
      const q = this.questions.find((x) => x.id === a.id)
      if (!q) return
      ;(byAxis[q.axis] ||= []).push(a.chosen + 1)
    })
    const levels = {}
    Object.keys(byAxis).forEach((key) => {
      const arr = byAxis[key]
      levels[key] = this.clamp(Math.round(arr.reduce((s, n) => s + n, 0) / arr.length))
    })
    return levels
  }

  overallLabel(levels) {
    const values = Object.values(levels)
    if (!values.length) return ""
    const avg = this.clamp(Math.round(values.reduce((s, n) => s + n, 0) / values.length))
    return this.levelLabels[avg]
  }

  clamp(n) {
    return Math.min(4, Math.max(1, n))
  }

  // Radar SVG (n axes, niveaux 1..4). Géométrie dupliquée côté serveur
  // (_radar.html.erb, tâche suivante) — compromis assumé.
  radarSvg(levels) {
    const size = 260, c = size / 2, R = 92, n = this.axes.length
    const point = (i, r) => {
      const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n
      return [c + r * Math.cos(angle), c + r * Math.sin(angle)]
    }
    const poly = (pts) => pts.map((p) => p.map((v) => v.toFixed(1)).join(",")).join(" ")

    let grid = ""
    for (let lvl = 1; lvl <= 4; lvl++) {
      grid += `<polygon points="${poly(this.axes.map((_, i) => point(i, (R * lvl) / 4)))}" fill="none" stroke="currentColor" stroke-opacity="0.12" />`
    }
    let spokes = ""
    this.axes.forEach((a, i) => {
      const [x, y] = point(i, R)
      const [lx, ly] = point(i, R + 16)
      const anchor = lx > c + 1 ? "start" : lx < c - 1 ? "end" : "middle"
      const tx = anchor === "start" ? lx + 3 : anchor === "end" ? lx - 3 : lx
      spokes += `<line x1="${c}" y1="${c}" x2="${x.toFixed(1)}" y2="${y.toFixed(1)}" stroke="currentColor" stroke-opacity="0.12" />`
      spokes += `<text x="${tx.toFixed(1)}" y="${ly.toFixed(1)}" font-size="9" text-anchor="${anchor}" dominant-baseline="middle" fill="currentColor" fill-opacity="0.7">${a.short}</text>`
    })
    const dataPts = this.axes.map((a, i) => point(i, (R * (levels[a.key] || 0)) / 4))
    const data = `<polygon points="${poly(dataPts)}" fill="rgb(59 130 246 / 0.25)" stroke="rgb(37 99 235)" stroke-width="2" />`

    // viewBox élargi horizontalement pour laisser la place aux libellés d'axes.
    return `<svg viewBox="-30 0 320 ${size}" class="mx-auto h-64 w-full max-w-xs text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg">${grid}${spokes}${data}</svg>`
  }
}
